// Localização: ./src/hooks/usePacientes.js (Versão 3.2.0 Melhorias em tratamento de erros)

import { useState, useCallback, useEffect } from 'react';
import api from '../api/api';

// URL base da sua API
// APENAS O CAMINHO, POIS O BASE_URL ESTÁ CONFIGURADO EM api.js
const API_URL = '/pacientes'; 

// Tipos de erro
export const ERROR_TYPES = {
    VALIDATION: 'validation',
    NETWORK: 'network',
    SERVER: 'server',
    PERMISSION: 'permission',
    GENERIC: 'generic'
};

/**
 * Categoriza um erro HTTP e retorna informações detalhadas
 * @param {Error} err - Erro capturado
 * @returns {{type: string, message: string, details: Array<string>}}
 */
const categorizeError = (err) => {
    // Erro de rede (sem resposta do servidor)
    if (!err.response) {
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            return {
                type: ERROR_TYPES.NETWORK,
                message: 'Tempo de conexão esgotado. Verifique sua internet e tente novamente.',
                details: [
                    'O servidor demorou muito para responder.',
                    'Verifique sua conexão com a internet.',
                    'Tente novamente em alguns instantes.'
                ]
            };
        }
        
        if (err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
            return {
                type: ERROR_TYPES.NETWORK,
                message: 'Erro de conexão. Não foi possível conectar ao servidor.',
                details: [
                    'Verifique se o servidor está rodando.',
                    'Verifique sua conexão com a internet.',
                    'Tente novamente em alguns instantes.'
                ]
            };
        }
        
        return {
            type: ERROR_TYPES.NETWORK,
            message: 'Erro de conexão. Verifique sua internet e tente novamente.',
            details: []
        };
    }
    
    const status = err.response.status;
    const data = err.response.data || {};
    const backendMessage = data.message || '';
    const backendDetails = data.details || [];
    const backendErrors = data.errors || {};
    
    // Erro de validação (400)
    if (status === 400) {
        const details = [];
        
        // Se houver erros específicos de campos, adiciona aos detalhes
        if (Object.keys(backendErrors).length > 0) {
            Object.entries(backendErrors).forEach(([field, messages]) => {
                if (Array.isArray(messages)) {
                    messages.forEach(msg => details.push(`${field}: ${msg}`));
                } else {
                    details.push(`${field}: ${messages}`);
                }
            });
        }
        
        // Adiciona detalhes do backend se existirem
        if (Array.isArray(backendDetails)) {
            details.push(...backendDetails);
        }
        
        return {
            type: ERROR_TYPES.VALIDATION,
            message: backendMessage || 'Dados inválidos. Verifique os campos do formulário.',
            details: details.length > 0 ? details : []
        };
    }
    
    // Erro de permissão (403)
    if (status === 403) {
        return {
            type: ERROR_TYPES.PERMISSION,
            message: backendMessage || 'Você não tem permissão para realizar esta ação.',
            details: [
                'Verifique se você tem as permissões necessárias.',
                'Entre em contato com o administrador se necessário.'
            ]
        };
    }
    
    // Erro não autorizado (401)
    if (status === 401) {
        return {
            type: ERROR_TYPES.PERMISSION,
            message: 'Sessão expirada. Faça login novamente.',
            details: []
        };
    }
    
    // Erro não encontrado (404)
    if (status === 404) {
        return {
            type: ERROR_TYPES.SERVER,
            message: backendMessage || 'Recurso não encontrado.',
            details: []
        };
    }
    
    // Erro do servidor (500, 502, 503, etc.)
    if (status >= 500) {
        return {
            type: ERROR_TYPES.SERVER,
            message: backendMessage || 'Erro interno do servidor. Tente novamente mais tarde.',
            details: [
                'O servidor encontrou um erro ao processar sua solicitação.',
                'Tente novamente em alguns instantes.',
                'Se o problema persistir, entre em contato com o suporte.'
            ]
        };
    }
    
    // Outros erros
    return {
        type: ERROR_TYPES.GENERIC,
        message: backendMessage || 'Ocorreu um erro inesperado. Tente novamente.',
        details: []
    };
};

// Hook customizado para gerenciar a lista de pacientes
export const usePacientes = () => {
    const [pacientes, setPacientes] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Carregamento inicial
    const [refreshing, setRefreshing] = useState(false); // Atualização após ação (CRUD)
    const [error, setError] = useState(null); // Pode ser string ou objeto {type, message, details}

    // Função para buscar a lista de pacientes
    const fetchPacientes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // 🛑 USAR A INSTÂNCIA 'api'
            const response = await api.get(API_URL);
            
            let allPacientes = response.data.data || response.data; // Ajuste se necessário

            // 💡 NOVO: FILTRAGEM DE PACIENTES ATIVOS
            //const pacientesAtivos = allPacientes.filter(paciente => {
            //    return paciente.status === 'Ativo'
 
            //});
            
            setPacientes(allPacientes);
            
        } catch (err) {
            console.error('Erro ao buscar pacientes:', err);
            const errorInfo = categorizeError(err);
            setError(errorInfo);
        } finally {
            setIsLoading(false);
        }
    }, []);


    // 💡 NOVO: Função para buscar um paciente individualmente pelo ID
    const fetchPacienteById = useCallback(async (id) => {
        try {
            // Rota GET /api/pacientes/:id
            const response = await api.get(`${API_URL}/${id}`);
            // A estrutura do backend retorna o paciente diretamente no corpo
            return response.data; // Retorna o objeto paciente
        } catch (err) {
            console.error(`Erro ao buscar paciente ID ${id}:`, err);
            // Propaga o erro para ser tratado no componente chamador (DiarioCuidadosPage)
            throw new Error(err.response?.data?.message || 'Falha ao carregar dados do paciente.');
        }
    }, []);
    
    
    
    // Função para criar ou atualizar um paciente
    const savePaciente = async (formData, isEditing) => { // Renomeando data para formData para clareza
        setRefreshing(true);
        setError(null);

        // 🛑 CONFIGURAÇÃO PARA FORÇAR AXIOS/NAVEGADOR A USAR MULTIPART CORRETAMENTE
        // Ao usar FormData com arquivos, o Content-Type DEVE ser definido pelo navegador
        // para incluir o "boundary". Definir como 'multipart/form-data' ou qualquer coisa 
        // fixa no Axios pode quebrar isso. Definir como 'undefined' ou simplesmente não incluí-lo,
        // mas aqui forçamos a remoção de qualquer Content-Type que o 'api' possa ter padronizado.
        const config = {
            headers: {
                'Content-Type': undefined, 
            },
        };

        try {
            if (isEditing) {
                // UPDATE (PUT) - Enviando formData e a config
                await api.put(`${API_URL}/${formData.get('id')}`, formData, config); 
            
            } else {
                // CREATE (POST) - Enviando formData e a config
                await api.post(API_URL, formData, config);
            }
        
            // 2. Atualiza a lista após sucesso
            await fetchPacientes();
            // setRefreshing(false); // Será chamado no 'finally'
            return true; // Sucesso
        
        } catch (err) {
            console.error('Erro ao salvar paciente:', err.response?.data || err);
            const errorInfo = categorizeError(err);
            setError(errorInfo);
            
            return false; // Falha
        } finally {
            // 🛑 GARANTIA: O refreshing DEVE ser resetado, ocorrendo sucesso ou falha
            setRefreshing(false); 
        }
    };
    
    // Função para deletar (desativar) um paciente
    const deletePaciente = async (id) => {
        setRefreshing(true);
        setError(null);
        try {
            // 🛑 USAR A INSTÂNCIA 'api'
            await api.delete(`${API_URL}/${id}`); 
            
            // 1. Atualiza a lista após sucesso
            await fetchPacientes();
            
            setRefreshing(false);
            return true;
        } catch (err) {
            setRefreshing(false);
            console.error('Erro ao deletar paciente:', err);
            const errorInfo = categorizeError(err);
            setError(errorInfo);
            return false;
        }
    };

    return {
        pacientes,
        isLoading,
        refreshing,
        error,
        fetchPacientes,
        fetchPacienteById,
        savePaciente,
        deletePaciente,
        setError
    };
};